import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  CardContent,
  TextField,
  Button,
  makeStyles,
  Container,
  Chip,
  Avatar,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import PaymentOptions from "./PaymentOptions";
import ClientInfoCard from "./ClientInfoCard";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { ArrowBack } from "@material-ui/icons"; // Import the back icon
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import AddIcon from "@material-ui/icons/Add";
import { useCart } from "../Context/CartContext";
import { formatFinalAmount } from "../utils/currency";
import { useShift } from "../Context/ShiftContext";
import { StaffCommissionDetailContext } from "../Context/StaffCommissionDetailsContext";
import { initialState } from "../initialState";
import { useSnackbar } from "../utils/useSnackbar";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useCurrency } from "../Context/CurrencyContext";
import { useDiscount } from "../Context/DiscountContext";
import {
  createInvoice,
  updateInvoice,
} from "../actions/invoiceActions";
import { updateStaffCommissionDetails } from "../actions/staffCommissionDetailActions";
// import { updateItems } from "../actions/itemActions";
import { getAllClients } from "../actions/clientActions";
import SaveIcon from "@material-ui/icons/Save";
import { useOrderSession } from "../Context/OrderSessionContext";
import BillDialog from "./BillDialog";
import CardDialog from "./CardDialog";
import { createStock, deleteStockById } from "../actions/stockActions";
import AddClient from "../components/Invoice/AddClient";
import { StockContext } from "../Context/StockContext";
import { useEffectiveProfile } from "../utils/useEffectiveProfile";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
const useStyles = makeStyles((theme) => ({
  card: {
    // Sits as a direct child of PosMainScreen's flex row, which stretches
    // children to full height by default — without this the card's own
    // background/shadow used to stretch the full viewport height, leaving
    // a huge dead white area below the actual content. Centering it in the
    // available space instead looks intentional at any screen height.
    alignSelf: "center",
    width: "min(640px, calc(100% - 32px))",
    maxWidth: 640,
    margin: "24px auto",
    padding: "24px 32px 28px",
    borderRadius: 20,
    backgroundColor: "#ffffff",
    border: "1.5px solid #e2e8f0",
    boxShadow: "0 20px 45px rgba(15, 23, 42, 0.09)",
    // Anchors processingOverlay below (position: absolute, inset: 0).
    position: "relative",
    "& .MuiContainer-root": {
      paddingLeft: 0,
      paddingRight: 0,
    },
    [theme.breakpoints.down("xs")]: {
      width: "calc(100% - 24px)",
      margin: "16px 12px",
      padding: "16px 16px 22px",
      borderRadius: 16,
    },
  },
  cashCheckout: {
    flex: 1,
    width: "100%",
    maxWidth: "none",
    margin: 0,
    padding: 0,
    backgroundColor: "transparent",
    boxShadow: "none",
    borderRadius: 0,
    position: "relative",
    "& .MuiCardContent-root": {
      padding: 0,
    },
  },
  // Frosted-glass "working on it" cover shown over the whole checkout card
  // while handleSubmit is mid-flight - Pay Now used to give no feedback at
  // all between the click and either a snackbar error or the navigate-away
  // on success, so a slow network made it look like the click hadn't
  // registered (and nothing stopped a second click in that window).
  processingOverlay: {
    position: "absolute",
    inset: 0,
    zIndex: 20,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
    borderRadius: "inherit",
    backgroundColor: "rgba(255, 255, 255, 0.55)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },
  processingText: {
    fontWeight: 700,
    color: "#0f172a",
  },
  checkoutHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: "1px solid #f1f5f9",
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(1),
    },
  },
  checkoutTitle: {
    fontSize: "1.25rem",
    fontWeight: 800,
    color: "#0f172a",
    letterSpacing: "-0.01em",
  },
  checkoutSubtitle: {
    fontSize: "0.8rem",
    color: "#64748b",
    marginTop: 2,
  },
  totalDuePill: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    borderRadius: 10,
    padding: "6px 14px",
    [theme.breakpoints.down("xs")]: {
      alignItems: "flex-start",
      width: "100%",
    },
  },
  totalDueLabel: {
    fontSize: "0.68rem",
    fontWeight: 700,
    color: "#ea580c",
    letterSpacing: "0.05em",
  },
  totalDueAmount: {
    fontSize: "1.2rem",
    fontWeight: 800,
    color: "#c2410c",
    fontVariantNumeric: "tabular-nums",
  },
  formControl: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  customerRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  customerField: {
    flex: 1,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: "#f8fafc",
      "&:hover": {
        backgroundColor: "#ffffff",
      },
    },
  },
  newCustomerButton: {
    borderRadius: 10,
    minHeight: 40,
    padding: theme.spacing(0.75, 1.75),
    fontWeight: 700,
    textTransform: "none",
    fontSize: "0.82rem",
    color: "#ea580c",
    borderColor: "#fdba74",
    backgroundColor: "#fff7ed",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#ffedd5",
      borderColor: "#f97316",
    },
  },
  newCustomerChip: {
    borderRadius: 20,
    fontWeight: 600,
    borderColor: "#e7edf3",
    transition: "border-color 0.18s ease, background-color 0.18s ease",
    "&:hover": {
      backgroundColor: "#fff7ef",
      borderColor: "#f6a561",
    },
  },
  invoiceBanner: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    margin: "18px auto 14px",
    padding: "8px 20px",
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    width: "fit-content",
  },
  invoiceBannerLabel: {
    fontSize: "0.78rem",
    fontWeight: 700,
    color: "#64748b",
    letterSpacing: "0.06em",
  },
  invoiceBannerDot: {
    color: "#cbd5e1",
    fontSize: "1rem",
  },
  invoiceBannerNum: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#1e293b",
    fontVariantNumeric: "tabular-nums",
  },
  backButton: {
    position: "absolute",
    top: theme.spacing(17),
    left: theme.spacing(28),
  },
  saveButton: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1.5),
  },
  payNowBtn: {
    minHeight: 46,
    width: "100%",
    maxWidth: 320,
    borderRadius: 12,
    fontWeight: 700,
    fontSize: "1rem",
    textTransform: "none",
    background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
    color: "#ffffff",
    boxShadow: "0 8px 20px rgba(234, 88, 12, 0.32)",
    transition: "all 0.18s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #ea580c 0%, #c2410c 100%)",
      boxShadow: "0 10px 24px rgba(234, 88, 12, 0.42)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  },
}));
// Leftover pre-react-toastify options - "bottom" isn't a valid
// react-toastify position (the library ignored it, so this actually
// rendered wherever its own internal default landed, not the app's shared
// bottom-right), and the style/closeStyle fields are silently ignored too
// (see ItemCards.js).
const PaymentDetails = ({ invoiceId }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const {
    cartItems,
    setCartItems,
    getTotalPrice,
    getRoundOff,
    receivedAmount,
    setReceivedAmount,
    calculateVat,
    getSubTotalPrice,
    calculateDiscount,
    remainingAfterProposedSale,
    clearInvoiceStockCredit,
    setIsCartSubmitting,
  } = useCart();
  const { myActiveShift, isShiftBlocked } = useShift();
  const { getCurrentStockQty, getConvertedQuantity } = useContext(StockContext);
  const effectiveProfile = useEffectiveProfile();
  const allowNegativeStock = effectiveProfile?.allowNegativeStock === true;
  // "Back-Date Invoice Creation" Special Feature (see GlobalSettingsModel.js)
  // - default OFF, gate on `=== true` not the `!== false` convention used
  // for the older *Entitled fields.
  const backDateEnabled = effectiveProfile?.backDateInvoiceEntitled === true;
  // `|| 7` would silently turn the "no limit" sentinel (0) back into 7 - 0
  // is falsy, so it must be checked for explicitly, not defaulted away.
  const rawMaxDaysBack = effectiveProfile?.backDateInvoiceMaxDays;
  const maxDaysBack = rawMaxDaysBack == null ? 7 : Number(rawMaxDaysBack);
  const backDateMinDate = maxDaysBack === 0
    ? undefined
    : new Date(Date.now() - maxDaysBack * 24 * 60 * 60 * 1000);

  const roundMoney = (value) => parseFloat((Number(value) || 0).toFixed(2));

  const isNamedCustomer = (selected) => {
    if (!selected || !selected._id) return false;
    const name = String(selected.name || "").trim().toLowerCase();
    if (!name || name === "cash") return false;
    if (defaultClient?._id && String(selected._id) === String(defaultClient._id)) {
      return false;
    }
    return true;
  };

  const previousAmountPaid = (doc) => {
    if (!doc) return 0;
    const fromRecords = roundMoney(
      (doc.paymentRecords || []).reduce(
        (sum, record) => sum + Number(record.amountPaid || 0),
        0
      )
    );
    const fromField = roundMoney(doc.totalAmountReceived);
    return Math.max(fromRecords, fromField);
  };

  const paymentStatusFor = (paid, billTotal) => {
    if (paid <= 0) return "Due";
    if (paid >= billTotal) return "Paid";
    return "Partial";
  };

  const buildPaymentSettlement = () => {
    const billTotal = roundMoney(getTotalPrice());
    const mode = invoiceData.paymentMode;
    const received = roundMoney(receivedAmount);

    if (mode === "Credit") {
      const paid = invoice
        ? Math.min(previousAmountPaid(invoice), billTotal)
        : 0;
      return {
        paymentMode: "Credit",
        status: paymentStatusFor(paid, billTotal),
        totalAmountReceived: paid,
        receivedAmount: null,
        change: null,
        paymentRecords: invoice && paid > 0 ? invoice.paymentRecords || [] : [],
      };
    }

    if (mode === "Cash" && received < billTotal) {
      const paid = Math.max(0, received);
      return {
        paymentMode: "Credit",
        status: paymentStatusFor(paid, billTotal),
        totalAmountReceived: paid,
        receivedAmount: paid,
        change: 0,
        paymentRecords:
          paid > 0
            ? [
                {
                  amountPaid: paid,
                  datePaid: new Date(),
                  paymentMethod: "Cash",
                  note: "POS checkout",
                  paidBy: client?.name || "",
                },
              ]
            : [],
      };
    }

    return {
      paymentMode: mode,
      status: "Paid",
      totalAmountReceived: billTotal,
      receivedAmount: mode === "Cash" ? received : null,
      change: mode === "Cash" ? roundMoney(Math.max(0, received - billTotal)) : null,
      paymentRecords: [],
    };
  };
  // VAT % actually applied to a cart line right now, and whether that rate is
  // baked into the price or added on top — captured onto the invoice at sale
  // time so returns can always recover exactly what was charged, even after
  // the item's or store's tax configuration changes later.
  const getItemVatRate = (cartItem) =>
    effectiveProfile?.vatOptions === "common"
      ? parseFloat(effectiveProfile?.commonVatRate) || 0
      : parseFloat(cartItem?.vat) || 0;
  const invoiceVatType = effectiveProfile?.vatType === "inclusive" ? "inclusive" : "exclusive";
  const { staffCommissionDetailsArray } = useContext(
    StaffCommissionDetailContext
  );
  const [invoiceNumber, setInvoiceNumber] = useState("");
  // Back-date picker's value, only used/shown when backDateEnabled - kept
  // separate from any due-date state elsewhere, this is the invoice's own
  // transaction date.
  const [invoiceBackDate, setInvoiceBackDate] = useState(new Date());
  // Drives the frosted-glass overlay over the checkout card while
  // handleSubmit is mid-flight (see its try/finally at the top).
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const { currency, updateCurrency } = useCurrency();
  const [invoiceData, setInvoiceData] = useState(initialState);
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const [error, setError] = useState("");
  const { invoice } = useSelector((state) => state.invoices);
  console.log("INvoice in payment details", invoice);
  const user = JSON.parse(localStorage.getItem("profile"));
  const clients = useSelector((state) => state.clients.clients);
  const [client, setClient] = useState(null);
  // When set, the New/Edit Customer dialog opens pre-filled for this client
  // and saves via updateClient instead of createClient. Null = create mode.
  const [editingClient, setEditingClient] = useState(null);
  const [defaultClient, setDefaultClient] = useState(null);
  const {
    discountFlag,
    clearDiscount,
    discountVal,
    discountType,
    discountRate,
  } = useDiscount();
  const [itemsData, setItemsData] = useState([]);
  const [openBillDialog, setOpenBillDialog] = useState(false);
  const [openCardDialog, setOpenCardDialog] = useState(false);
  const handleBillDialogClose = () => {
    // You can add logic to close the dialog and handle bill printing here
    setOpenBillDialog(false);
  };
  const dispatch = useDispatch();
  const history = useHistory();
  const { activeSessionId, checkoutSession } = useOrderSession();
  useEffect(() => {
    const defaultClient = clients.filter((clt) => clt.name === "Cash");
    console.log("Default Client", defaultClient);
    setDefaultClient(defaultClient[0]);
  }, [clients]);

  useEffect(() => {
    dispatch(
      // getClientsByUser({ search: user?.result._id || user?.result?.googleId })
      getAllClients()
    );
    // dispatch(
    //   // getClientsByUser({ search: user?.result._id || user?.result?.googleId })
    //   getAllSuppliers()
    // );

    // dispatch(
    //   // getItemsByUser({ search: user?.result._id || user?.result?.googleId })
    //   getAllItems()
    // );
    // eslint-disable-next-line
  }, [dispatch]);

  // useEffect(() => {
  //   const transformedItems = cartItems.map((cartItem) => ({
  //     item: cartItem._id, // Extract _id
  //     quantity: cartItem.quantity, // Extract quantity
  //   }));
  //   console.log("useffect run in payment details")
  //   setInvoiceData({ ...invoiceData, items: transformedItems });
  // }, [cartItems,setInvoiceData]);
  useEffect(() => {
    return () => {
      setIsCartSubmitting?.(false);
    };
  }, [setIsCartSubmitting]);

  useEffect(() => {
    if (invoice && invoice.items && invoice.items.length > 0) {
      // Extracting _id and name from each item and creating a new array of objects
      const extractedData = invoice.items.map((item) => ({
        _id: item._id,
        quantity: item.quantity,
      }));
      setItemsData(extractedData);
      setInvoiceNumber(invoice.invoiceNumber);
      setInvoiceData(invoice);
      setClient(invoice.client);
      // Back-date picker defaults to this invoice's OWN already-saved date
      // (never "today") when reopening it for edit - the Special Feature
      // lets it be corrected from here, not reset.
      setInvoiceBackDate(invoice.createdAt ? new Date(invoice.createdAt) : new Date());
    }
  }, [invoice]);
  // Was: a separate `status` state, set by this effect reacting to
  // invoiceData.paymentMode changing - since the effect runs a render cycle
  // AFTER the paymentMode update, submitting the invoice before that cycle
  // completed could send status's *initial* value ("", from useState("")
  // below) instead of "Paid"/"Due". Every downstream page that treats
  // anything other than the literal string "Paid" as a due/credit invoice
  // (see CreditInvoices.js's `row.status === "Paid" ? false : ...` filter)
  // would then wrongly list a fully-paid Cash sale as due. Computing the
  // status inline at submit time (see handleSubmit below) instead of
  // through this effect removes the race entirely.
  // invoiceNumber is intentionally left blank here for a new sale - it's
  // assigned atomically server-side on save (see createInvoice in
  // server/controllers/invoices.js), never guessed on the client. The
  // "CURRENT INVOICE" banner below just stays hidden until then, and stock
  // transactions are tagged with the real number returned by the server
  // (see handleSubmit's create branch), not this local guess.
  const stockDetailsItems = async (cartItems, invoiceNumber, createdAtOverride, invoiceObjectId) => {
    // debugger;
    // Staff-required (service) items and items with stock tracking switched
    // off were never meant to carry a units.stock entry at all - passing
    // them into getConvertedQuantity tripped its "Invalid item structure"
    // guard and, worse, its `|| item.quantity` fallback then recorded a
    // *positive* SALES quantity (increasing stock) instead of skipping the
    // item entirely. Matches the same isStaffReq/!toggleStock skip already
    // used by the stock-availability check earlier in handleSubmit.
    const stockItems = await Promise.all(
      cartItems
        .filter((item) => !item.isStaffReq && item.toggleStock)
        .map(async (item) => {
          const converted = getConvertedQuantity(item, "sale", null, false);
          const qty =
            converted === 0 || converted == null
              ? -Math.abs(Number(item.quantity) || 0)
              : converted;
          return {
            item: item._id,
            transactionType: "SALES",
            qty,
            categoryId: item.category?.categoryId?._id || item.category?.categoryId,
            invoiceId: invoiceNumber,
            // Back-dated invoices (Special Feature, see GlobalSettingsModel.js)
            // must post stock on the SAME date as the invoice, or stock-level
            // reports for that past date wouldn't reflect this sale.
            // createdAtOverride (the server's just-saved value) must win over
            // stale Redux `invoice.createdAt` - an edit that also corrects
            // the date would otherwise keep re-posting stock under the OLD
            // date, permanently disagreeing with the invoice.
            createdAt: createdAtOverride || invoice?.createdAt || new Date(),
            unit: item.units?.sale?.unit?._id || item.units?.sale?.unit,
            references: {
              invoiceId: invoiceNumber,
              invoiceObjectId,
              transactionRef: "Invoice",
            },
            creator: user?.result?._id || "65549a5b77d1c64130b5db0c",
          };
        })
    );

    return stockItems;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const hasEmptyItems = invoiceData.items.some(
      (item) => !item.quantity && !item.paymentMode
    );

    // console.log(hasEmptyItems);
    if (
      hasEmptyItems ||
      // !subTotal ||
      !getTotalPrice

      // !vat ||
      // !rates ||
      //  !currency ||
      // !selectedDate ||
      // !client
      // !signatureDataUrl||
      // !supplier
      // !type ||
    ) {
      openSnackbar("Please fill the details");
      return;
    }
    if (isShiftBlocked) {
      openSnackbar("Please start your shift before creating an invoice.");
      return;
    }
    if (invoiceData.paymentMode === "") {
      openSnackbar("Please select the Payment Mode");
      return;
    }
    if (invoiceData.paymentMode === "Cash" && !receivedAmount) {
      openSnackbar("Please fill the Amount");
      return;
    }
    if (
      invoiceData.paymentMode === "Cash" &&
      roundMoney(receivedAmount) < roundMoney(getTotalPrice()) &&
      !isNamedCustomer(client)
    ) {
      openSnackbar("Please pay the required amount, or select a customer for partial payment");
      return;
    }
    if (
      (invoiceData.paymentMode === "Credit" ||
        (invoiceData.paymentMode === "Cash" &&
          roundMoney(receivedAmount) < roundMoney(getTotalPrice()))) &&
      !isNamedCustomer(client)
    ) {
      openSnackbar("Please select the customer");
      return;
    }
    setError("");
    // Everything past this point is the actual submission - covers both
    // branches below (session checkout, and update/create). Reset at every
    // exit point past here: the stock-check bail-out, the end of the
    // session-checkout branch, the edit branch's catch, and the natural end
    // of the function (covers the edit-success and both create-branch
    // outcomes, none of which have their own early return).
    setIsProcessingPayment(true);

    // Final safety-net stock check: stock can change between "add to cart"
    // and checkout (another till selling the same item, a stock adjustment,
    // etc.), so re-validate every stock-tracked line right before the
    // invoice/stock transaction is created. Reuses the same
    // getCurrentStockQty/getConvertedQuantity calculation CartContext.js uses -
    // no separate stock API. Skipped entirely when Allow Negative Stock is on.
    if (!allowNegativeStock) {
      for (const cartItem of cartItems) {
        if (cartItem.isStaffReq || !cartItem.toggleStock) continue;
        const stock = await getCurrentStockQty(cartItem._id);
        const convertedQty = await getConvertedQuantity(
          cartItem,
          "sale",
          cartItem.units?.stock?.unit?._id
        );
        const remainingStock = remainingAfterProposedSale(
          stock.currentStock,
          cartItem._id,
          convertedQty
        );
        if (remainingStock < 0) {
          openSnackbar(
            `Not enough stock for ${cartItem.itemName || "an item"}. Please adjust the quantity before completing the sale.`
          );
          setIsProcessingPayment(false);
          return;
        }
      }
    }

    const settlement = buildPaymentSettlement();

    if (activeSessionId) {
      try {
        const paymentPayload = {
          client: client ? client : defaultClient,
          paymentMode: settlement.paymentMode,
          discount: discountVal ? parseFloat(discountVal) : 0,
          receivedAmount: settlement.receivedAmount,
          change: settlement.change,
          status: settlement.status,
          totalAmountReceived: settlement.totalAmountReceived,
          paymentRecords: settlement.paymentRecords,
          currency,
          creator: [user?.result?._id || user?.result?.googleId],
          shiftId: myActiveShift?._id ?? null,
          employeeId: myActiveShift?.employeeId ?? null,
          employeeName: myActiveShift?.employeeName ?? null,
        };
        const { invoice: createdInvoice } = await checkoutSession(activeSessionId, paymentPayload);
        setCartItems([]);
        clearDiscount?.();
        setReceivedAmount("");
        // The payment screen is just the /payment route - navigating away
        // from it here is the only "close" step needed, no separate view
        // state to reset.
        history.push(`/invoice/${createdInvoice._id}`);
      } catch (err) {
        openSnackbar(err?.response?.data?.message || "Checkout failed");
      }
      setIsProcessingPayment(false);
      return;
    }

    const invoiceItems = cartItems.map((cartItem) => {
      const originalRate =
        cartItem.originalPrice ??
        cartItem?.units?.sale?.price ??
        cartItem?.rate ??
        0;
      const effectiveRate =
        cartItem.overridePrice != null
          ? parseFloat(cartItem.overridePrice)
          : originalRate;
      const priceAdjusted =
        Math.abs(effectiveRate - originalRate) > 0.0001;
      return {
        item: cartItem._id,
        quantity: cartItem.quantity,
        rate: effectiveRate,
        originalRate,
        priceAdjusted,
        vatRate: getItemVatRate(cartItem),
        remark: (cartItem.remark && String(cartItem.remark).trim()) || null,
        customerNote: (cartItem.customerNote && String(cartItem.customerNote).trim()) || null,
      };
    });

    const {
      paymentRecords: _oldPaymentRecords,
      totalAmountReceived: _oldTotalReceived,
      status: _oldStatus,
      paymentMode: _oldPaymentMode,
      receivedAmount: _oldReceivedAmount,
      change: _oldChange,
      businessDetails,
      ...invoiceRest
    } = invoiceData;

    const invoicePayload = {
      ...invoiceRest,
      items: invoiceItems,
      subTotal: getSubTotalPrice(),
      total: getTotalPrice(),
      roundOff: getRoundOff(),
      invoiceNumber,
      vat: calculateVat(),
      vatType: invoiceVatType,
      discount: discountVal ? discountVal.toFixed(2) : 0,
      currency: currency,
      ...settlement,
      client: client ? client : defaultClient,
      // Preserve the original creator when re-saving an existing invoice -
      // this used to always stamp the CURRENT (checkout) user, so "Created
      // By" silently flipped to whoever last completed/edited the sale.
      creator: invoice?.creator || [user?.result?._id || user?.result?.googleId],
      shiftId: myActiveShift?._id ?? null,
      employeeId: myActiveShift?.employeeId ?? null,
      employeeName: myActiveShift?.employeeName ?? null,
      // Back-Date Invoice Creation Special Feature - covers both a new sale
      // AND correcting an existing invoice's date on reopen. Falls back to
      // the invoice's own unchanged date (or "today" for a new sale) when
      // the feature is off. The server re-validates/enforces this
      // regardless of what's sent here (see createInvoice/updateInvoice).
      createdAt: backDateEnabled ? invoiceBackDate : (invoice?.createdAt || new Date()),
      isPrintBill: true,
    };

    if (invoice) {
      // Keep the same invoice id (returns, stock details, payments all point
      // at it). Reverse the original SALES ledger rows, save the new lines,
      // then deduct current cart qty. The previous delete-invoice + recreate
      // path often restored old stock and never re-posted the sale.
      try {
        setIsCartSubmitting?.(true);
        await dispatch(deleteStockById(invoiceNumber, "SALES"));
        const updatedInvoice = await dispatch(updateInvoice(invoiceId, invoicePayload, openSnackbar));
        // updatedInvoice.createdAt is what the server actually saved (its
        // own back-date enforcement may have adjusted/rejected an edited
        // date) - stock must be re-posted against that real value, not the
        // local invoiceBackDate guess, so an edited invoice date and its
        // stock ledger entries always agree.
        const stockTransactions = await stockDetailsItems(
          cartItems,
          invoiceNumber,
          updatedInvoice?.createdAt,
          invoiceId
        );
        if (stockTransactions.length) {
          await dispatch(createStock(stockTransactions));
        }
        dispatch(updateStaffCommissionDetails(staffCommissionDetailsArray));
        setCartItems([]);
        clearInvoiceStockCredit();
        clearDiscount?.();
        setReceivedAmount("");
        history.push(`/invoice/${invoiceId}`);
      } catch (err) {
        setIsCartSubmitting?.(false);
        setIsProcessingPayment(false);
        openSnackbar(
          err?.response?.data?.message ||
            "Failed to update invoice stock. Please try again."
        );
        return;
      }
    } else {
      // Must be set BEFORE dispatching - createInvoice (invoiceActions.js)
      // calls history.push('/invoice/:id') itself the moment the save
      // succeeds, well before this call unwinds back here. Without this,
      // isCartSubmitting was still false at that exact instant, so
      // PosMainScreen's UnsavedChangesGuard (isSaleDirty depends on
      // !isCartSubmitting) saw a still-full cart and blocked that very
      // push with its own "Unsaved Changes" prompt - right after a
      // successful Pay Now. Same pattern the edit branch above already
      // uses; the natural unmount (this screen leaves as part of that
      // navigation) resets it back via the cleanup effect further up.
      setIsCartSubmitting?.(true);
      // Awaited (was previously fired-and-forgotten) so stock transactions
      // below can be tagged with the REAL, atomically-assigned invoiceNumber
      // the server just returned - never the local guess, which no longer
      // exists (see the comment above the removed getInvoiceNumber effect).
      const createdInvoice = await dispatch(
        createInvoice(
          invoicePayload,
          staffCommissionDetailsArray,
          history,
          openSnackbar
        )
      );
      // createInvoice already reported the failure to the user and returns
      // undefined on error (see the action creator) - skip posting stock for
      // an invoice that was never actually saved, same guard Purchase.js
      // uses around createPurchase.
      if (!createdInvoice?.invoiceNumber) {
        // Save failed and nothing navigated away - stop suppressing the
        // guard so it still protects this now-still-unsaved cart.
        setIsCartSubmitting?.(false);
      } else {
        // createdInvoice.createdAt is what the server actually saved (its
        // own back-date enforcement may have adjusted/rejected the client's
        // requested date) - stock must be posted against that real value,
        // not the local invoiceBackDate guess.
        const stockTransactions = await stockDetailsItems(
          cartItems,
          createdInvoice.invoiceNumber,
          createdInvoice.createdAt,
          createdInvoice._id
        );
        if (stockTransactions.length) {
          try {
            await dispatch(createStock(stockTransactions));
          } catch (err) {
            openSnackbar(
              err?.response?.data?.message ||
                "Invoice saved but stock was not deducted. Please check stock."
            );
          }
        }

        // New sale completed: don't leave this invoice's cart/payment state
        // hanging around for the next time the POS screen opens.
        setCartItems([]);
        clearInvoiceStockCredit();
        clearDiscount?.();
        setReceivedAmount("");
      }
    }

    setInvoiceData(initialState);
    setIsProcessingPayment(false);
  };
  const handleButton = () => {
    console.log("cartItems", cartItems);

    // const updatedCartItems = cartItems.map(item => {
    //   const quantity = parseFloat(item.quantity);
    //   const initialQty = parseFloat(item.stockQty.initialQty);

    //   // Check if the values are valid numbers before subtraction
    //   if (!isNaN(quantity) && !isNaN(initialQty)) {
    //     return {
    //       ...item,
    //       stockQty: {
    //         ...item.stockQty,
    //         initialQty: initialQty - quantity,
    //       },
    //     };
    //   } else {
    //     // Handle the case where the values are not valid numbers
    //     console.error('Invalid quantity or initialQty:', item);
    //     // You can choose to set a default value or skip updating the item
    //     return item; // or return a default value
    //   }
    // });
    // console.log("updated Items",updatedCartItems)
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "paymentMode") {
      setInvoiceData({
        ...invoiceData,
        [name]: value,
      });
      if (value === "Cash") {
        setOpenBillDialog(true);
      }
      if (value === "Card") {
        setOpenCardDialog(true);
      }
      // Check if "Cash" is selected and set openCashDialog accordingly
    }
  };

  const handleCardPayment = (updatedCardData) => {
    setInvoiceData({ ...invoiceData, card: updatedCardData });
  };
  const CustomPaper = (props) => {
    return <Paper elevation={3} {...props} />;
  };
  const clientsProps = {
    options: clients,
    getOptionLabel: (option) => option.name,
    // Without this, MUI compares by reference (===) and warns "none of the
    // options match" any time the selected client object isn't the exact
    // same instance as its entry in `clients` (e.g. right after creating or
    // quick-editing one, since the API's returned object and the Redux-store
    // copy are different instances even though they're the same customer).
    getOptionSelected: (option, value) => option._id === value?._id,
  };
  return (
    <>
      <AddClient
        setOpen={setOpen}
        open={open}
        editingClient={editingClient}
        onSaved={(saved) => {
          if (!saved) {
            setEditingClient(null);
            return;
          }
          if (!editingClient) {
            // Fresh customer: put them straight onto this invoice instead of
            // making the cashier search for the name they just typed.
            setClient(saved);
          } else if (client && client._id === editingClient._id) {
            // Quick-edited the customer already on this invoice: refresh the card.
            setClient(saved);
          }
          // Quick-editing someone else from the dropdown doesn't select them.
          setEditingClient(null);
        }}
      />
      <Card
        className={openBillDialog ? classes.cashCheckout : classes.card}
        elevation={openBillDialog ? 0 : 1}
      >
        {isProcessingPayment && (
          <div className={classes.processingOverlay} role="status" aria-live="polite">
            <CircularProgress size={40} style={{ color: "#ea580c" }} />
            <Typography className={classes.processingText}>
              {invoice ? "Updating invoice…" : "Processing payment…"}
            </Typography>
          </div>
        )}
        <CardContent>
          {/* <IconButton className={classes.backButton} onClick={()=>setOpenBillDialog(!openBillDialog)}>
          <ArrowBack />
        </IconButton> */}
          {/* Your existing form elements */}
          {/* ... */}

          <form onSubmit={handleSubmit}>
            {!openBillDialog && !openCardDialog && (
              <>
                <Container>
                  <div className={classes.checkoutHeader}>
                    <div>
                      <Typography className={classes.checkoutTitle}>Checkout</Typography>
                      <Typography className={classes.checkoutSubtitle}>
                        Select customer and payment method
                      </Typography>
                    </div>
                    <div className={classes.totalDuePill}>
                      <span className={classes.totalDueLabel}>TOTAL DUE</span>
                      <span className={classes.totalDueAmount}>
                        {currency}
                        {getTotalPrice ? formatFinalAmount(getTotalPrice()) : "0.00"}
                      </span>
                    </div>
                  </div>

                  {/* Back-Date Invoice Creation Special Feature (see
                      GlobalSettingsModel.js) - shown for a new sale AND
                      when reopening an existing invoice here, so its date
                      can be corrected too. Defaults to the invoice's own
                      already-saved date on reopen (see the effect above),
                      never "today". */}
                  {backDateEnabled && (
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                      <KeyboardDatePicker
                        autoOk
                        size="small"
                        margin="dense"
                        inputVariant="outlined"
                        id="invoice-back-date-picker"
                        label={invoice ? "Invoice Date (editing)" : "Invoice Date"}
                        format="MMM dd, yyyy"
                        value={invoiceBackDate}
                        onChange={(date) => setInvoiceBackDate(date || new Date())}
                        maxDate={new Date()}
                        minDate={backDateMinDate}
                        disableFuture
                        style={{ marginBottom: 8 }}
                        KeyboardButtonProps={{ "aria-label": "change invoice date" }}
                      />
                    </MuiPickersUtilsProvider>
                  )}

                  {client && (
                    <ClientInfoCard
                      client={client}
                      setClient={setClient}
                      onEdit={() => {
                        setEditingClient(client);
                        setOpen(true);
                      }}
                    />
                  )}
                  <div
                    style={client ? { display: "none" } : { display: "block" }}
                  >
                    <div className={classes.customerRow}>
                      <Autocomplete
                        {...clientsProps}
                        PaperComponent={CustomPaper}
                        className={classes.customerField}
                        renderOption={(option) => (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span>{option.name}</span>
                            <IconButton
                              size="small"
                              title={`Quick-edit ${option.name}`}
                              onClick={(event) => {
                                // Don't let this also select the option -
                                // it's a shortcut to edit, not to pick them.
                                event.stopPropagation();
                                setEditingClient(option);
                                setOpen(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" style={{ color: "#94a3b8" }} />
                            </IconButton>
                          </div>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Customer"
                            margin="none"
                            variant="outlined"
                            size="small"
                            placeholder="Search or pick customer..."
                          />
                        )}
                        value={client}
                        onChange={(event, value) => {
                          setClient(value);
                        }}
                      />
                      {!client && (
                        <Button
                          variant="outlined"
                          size="small"
                          className={classes.newCustomerButton}
                          onClick={() => {
                            setEditingClient(null);
                            setOpen(true);
                          }}
                          startIcon={<AddIcon />}
                        >
                          New Customer
                        </Button>
                      )}
                    </div>
                  </div>
                </Container>

                <Container>
                  <PaymentOptions
                    invoiceData={invoiceData}
                    handleInputChange={handleInputChange}
                  />
                  <div className={classes.invoiceBanner}>
                    <span className={classes.invoiceBannerLabel}>CURRENT INVOICE</span>
                    {invoiceNumber && (
                      <>
                        <span className={classes.invoiceBannerDot}>•</span>
                        <span className={classes.invoiceBannerNum}>#{invoiceNumber}</span>
                      </>
                    )}
                  </div>
                </Container>
              </>
            )}
            {openBillDialog && (
              <BillDialog
                currency={currency}
                setOpenBillDialog={setOpenBillDialog}
                invoice={invoice}
                allowPartialPay={isNamedCustomer(client)}
              />
            )}
            {openCardDialog && (
              <CardDialog
                handleCardPayment={handleCardPayment}
                invoiceData={invoiceData}
                currency={currency}
                setOpenCardDialog={setOpenCardDialog}
              />
            )}
            {!openBillDialog && (
              <div className={classes.saveButton}>
                <Button
                  variant="contained"
                  type="submit"
                  size="large"
                  className={classes.payNowBtn}
                  disabled={isProcessingPayment}
                  startIcon={<SaveIcon />}
                >
                  {invoice ? "UPDATE" : "Pay Now"}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </>
  );
};

export default PaymentDetails;
